import {MouseEvent, useCallback, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {
  Alert,
  MenuItem,
  Button,
  Menu,
  Stack,
  Typography,
  Avatar,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {Add, FileOpenOutlined} from '@mui/icons-material';
import {FiTrash2, FiEdit} from 'react-icons/fi';
import GenericHeader from '../../generic-header/generic-header';
import {RoleAutocomplete} from '../../role-autocomplete/role-autocomplete';
import {Option} from '../../role-autocomplete/autocomplete-input';
import {UsersRepository} from '../../../repositories/users';
import {CarePlan} from '../../../backend/interfaces/care-plan';
import {ActivitiesSector} from '../../../backend/interfaces/activities-sector';
import {ActivitiesGroup} from '../../../backend/interfaces/activities-group';
import {UserPublicInfo} from '../../../backend/responses/user-public-info';
import {Pallete} from '../../../theme/bluebird';
import {convertArrayToIndex} from '../../../utils/functions';
import {useObserveViewport} from '../../../hooks';
import {useSeniorSidebar} from '../../../contexts/seniors-sidebar/context';
import {ScreenPaths} from '../../../enums';
import {AiButton} from '../../ai-button/ai-button';
import {useAuth} from '../../../contexts/auth/context';
import {EntitySpecialPurpose} from '../../../backend/enums/entity-special-purpose';
import {SECTOR_ID_TO_PERMISSION_TYPE_MAPPING} from '../../../utils/constants';

interface CarePlanSheetHeaderProps {
  isLoading: boolean;
  activitiesSectorId: string;
  activitiesSectors?: ActivitiesSector[] | null;
  activitiesGroups?: Array<ActivitiesGroup>;
  carePlan?: CarePlan | null;
  canWrite: boolean;
  hasTemplates: boolean;
  onCarePlanSave: (carePlan: CarePlan) => void;
  onCarePlanDelete: () => void;
  onSaveTemplate: () => void;
  onImportTemplate: () => void;
  onActivitiesGroupSelected: (activitiesGroup: ActivitiesGroup) => void;
  onAiGeneratedRequest?: () => void;
}

export const CarePlanSheetHeader = ({
  isLoading,
  activitiesSectorId,
  activitiesSectors,
  activitiesGroups,
  carePlan,
  canWrite,
  hasTemplates,
  onCarePlanSave,
  onCarePlanDelete,
  onSaveTemplate,
  onImportTemplate,
  onActivitiesGroupSelected,
  onAiGeneratedRequest,
}: CarePlanSheetHeaderProps) => {
  const {t} = useTranslation();
  const {isRunningMobile} = useObserveViewport();
  const sidebarContext = useSeniorSidebar();
  const authContext = useAuth();
  const {hasReadPermission} = authContext;

  // Get params
  let {entityId, id}: {entityId: string; id: string} = useParams();

  // Hold state
  const [pageTitle, setPageTitle] = useState('');
  const [assignedCaregiver, setAssignedCaregiver] = useState<UserPublicInfo | null>(null);
  const [loadingAssingedCaregiver, setLoadingAssingedCaregiver] = useState(false);

  // Controlled activities group add menu
  const [addActivitiesGroupMenuAnchor, setAddActivitiesGroupMenuAnchor] =
    useState<null | HTMLElement>(null);
  const addActivitiesGroupMenuOpen = Boolean(addActivitiesGroupMenuAnchor);

  // Controlled activities group add menu
  const handleAddActivitiesGroupMenuClick = (event: MouseEvent<HTMLButtonElement>) =>
    setAddActivitiesGroupMenuAnchor(event.currentTarget);

  const handleAddActivitiesGroupMenuClose = () => setAddActivitiesGroupMenuAnchor(null);

  // Handle Caregiver re-assignement
  const handleCaregiverChange = (user: Option | undefined) => {
    if (!carePlan) return;

    if (user?.key === '') {
      onCarePlanSave({...carePlan, responsibleUserId: undefined});
    } else if (user?.key !== assignedCaregiver?.id) {
      onCarePlanSave({...carePlan, responsibleUserId: user?.key ?? undefined});
    }
  };

  const getAssignedCaregiverInfo = useCallback(async () => {
    try {
      if (!carePlan?.responsibleUserId) {
        setAssignedCaregiver(null);
        return;
      }

      setLoadingAssingedCaregiver(true);
      const response = await UsersRepository.instance.getInfoById(carePlan.responsibleUserId);
      setAssignedCaregiver(response);
    } catch (error) {
      console.error(error);
      setAssignedCaregiver(null);
    } finally {
      setLoadingAssingedCaregiver(false);
    }
  }, [carePlan]);

  const activitiesGroupList = useMemo(() => {
    if (!carePlan || !activitiesGroups) return [];

    let listedActivitiesGroups: string[] = [];

    carePlan?.activitiesGroups?.map((group) => {
      if (!listedActivitiesGroups.includes(group.activitiesGroupId))
        listedActivitiesGroups.push(group.activitiesGroupId);
      return undefined;
    });

    return activitiesGroups.filter((group) => !listedActivitiesGroups.includes(group.id ?? ''));
  }, [activitiesGroups, carePlan]);

  const indexedActivitiesSectors = useMemo(() => {
    if (activitiesSectors) return convertArrayToIndex<ActivitiesSector>(activitiesSectors, 'id');
    return undefined;
  }, [activitiesSectors]);

  const seniorName = useMemo(() => {
    const senior = sidebarContext.state.list?.get(id);
    return senior ? senior.originalData.displayName || senior.originalData?.name : '';
  }, [id, sidebarContext.state.list]);

  const defaultCarePlanLink = useMemo(() => {
    const firstAvailableSector = activitiesSectors?.find(({id: sectorId}) => {
      const permissionType = sectorId ? SECTOR_ID_TO_PERMISSION_TYPE_MAPPING[sectorId] : undefined;

      return permissionType && hasReadPermission(permissionType);
    });

    return firstAvailableSector?.id
      ? `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_CarePlan.replace(
          ':activitiesSectorId',
          firstAvailableSector.id
        )}`
      : '';
  }, [activitiesSectors, entityId, hasReadPermission, id]);

  useEffect(() => {
    if (indexedActivitiesSectors) {
      const sector = indexedActivitiesSectors[activitiesSectorId];
      if (sector) setPageTitle(sector.description || sector.name);
    }
  }, [activitiesSectorId, indexedActivitiesSectors, isLoading]);

  // if the careplan info changes, re-fetch assigned caregiver info
  useEffect(() => {
    getAssignedCaregiverInfo();
  }, [carePlan, getAssignedCaregiverInfo]);

  let actions;
  if (canWrite) {
    const deleteAction = {
      label: t(translations.generic_header.actions.delete),
      icon: {element: FiTrash2},
      callback: onCarePlanDelete,
    };
    const importAction = {
      label: t(translations.generic_header.actions.import),
      icon: {element: FileOpenOutlined},
      callback: onImportTemplate,
    };
    const saveAction = {
      label: t(translations.generic_header.actions.save),
      icon: {element: FiEdit},
      callback: onSaveTemplate,
    };

    if (carePlan?.activitiesGroups && carePlan.activitiesGroups.length > 0) {
      if (hasTemplates) actions = [saveAction, importAction, deleteAction];
      else actions = [saveAction, deleteAction];
    } else {
      if (hasTemplates) actions = [importAction];
    }
  }

  return (
    <GenericHeader
      isLoading={isLoading && !seniorName}
      titleCrumbs={
        isRunningMobile
          ? [{label: pageTitle, link: ''}]
          : [
              {
                label: t(translations.screen_paths.professional_caregiver_seniors),
                link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}`,
              },
              {
                label: seniorName,
                link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
              },
              {
                label: t(translations.screen_paths.senior_care_plan),
                link: defaultCarePlanLink,
              },
              {
                label: pageTitle,
                link: '',
              },
            ]
      }
      actions={actions ? {actions} : undefined}
      leftColumn={
        isLoading ? (
          <Skeleton width={200} height={38} />
        ) : (
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'center',
              height: '40px',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: 'white',
                fontSize: '16px',
                lineHeight: '19px',
                width: {xs: '50px', md: 'auto'},
              }}
              noWrap
            >
              {t(translations.seniors.edit.senior_dcm.careplan.caregiver.title)}:
            </Typography>

            {canWrite ? (
              <RoleAutocomplete
                defaultUserId={carePlan?.responsibleUserId}
                onChange={handleCaregiverChange}
              />
            ) : loadingAssingedCaregiver ? (
              <Skeleton width={80} height={30} />
            ) : (
              <Tooltip
                title={t(
                  translations.seniors.edit.senior_dcm.careplan.caregiver.unnasigned_tooltip
                )}
                placement="bottom"
                arrow
              >
                <Stack
                  flexDirection="row"
                  gap="6px"
                  alignItems="center"
                  sx={{
                    cursor: 'not-allowed',
                  }}
                >
                  {assignedCaregiver && (
                    <Avatar
                      sx={{height: '28px', width: '28px', border: '1px solid white'}}
                      src={assignedCaregiver.picture}
                      alt={`avatar_${assignedCaregiver?.name}`}
                    />
                  )}
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '24px',
                      color: 'white',
                    }}
                  >
                    {assignedCaregiver
                      ? assignedCaregiver?.name
                      : t(translations.seniors.edit.senior_dcm.careplan.caregiver.unnasigned)}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Stack>
        )
      }
      rightColumn={
        <Stack
          direction="row"
          gap={2}
          sx={{
            justifyItems: 'flex-end',
            justifyContent: 'flex-end',
            alignContent: 'flex-end',
          }}
        >
          {isLoading ? (
            <></>
          ) : (
            <div className="add-activities-groups">
              {activitiesGroups === null && (
                <Alert severity="error">
                  {t(translations.seniors.edit.senior_dcm.careplan.activities.load_error)}
                </Alert>
              )}

              {activitiesGroupList && (
                <>
                  {canWrite && (
                    <Button
                      aria-controls={addActivitiesGroupMenuOpen ? 'add-activities-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={addActivitiesGroupMenuOpen ? 'true' : undefined}
                      onClick={handleAddActivitiesGroupMenuClick}
                      variant="inverted"
                      size="large"
                      startIcon={<Add />}
                      disabled={activitiesGroupList.length === 0}
                      sx={{
                        width: isRunningMobile ? 'auto' : '146px',
                        height: '40px',
                        letterSpacing: '0.2px',
                        py: '8px',
                        '.MuiButton-startIcon': {
                          margin: isRunningMobile ? 0 : '0 8px 0 -4px',
                        },
                      }}
                    >
                      {!isRunningMobile &&
                        t(translations.seniors.edit.senior_dcm.careplan.activities.add_button)}
                    </Button>
                  )}
                  <Menu
                    id="add-activities-menu"
                    anchorEl={addActivitiesGroupMenuAnchor}
                    open={addActivitiesGroupMenuOpen}
                    onClose={handleAddActivitiesGroupMenuClose}
                    transformOrigin={{horizontal: 'right', vertical: 'top'}}
                    anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    sx={{
                      transformOrigin: 'bottom 20px',
                      width: '100%',
                      '.MuiList-root': {
                        padding: '4px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      },
                    }}
                  >
                    {activitiesGroupList.map((activitiesGroup: ActivitiesGroup) => {
                      if (!activitiesGroup.isActive) return undefined;

                      return (
                        <MenuItem
                          key={`activities-group-option-${activitiesGroup.id}`}
                          onClick={() => {
                            // Close Menu
                            setAddActivitiesGroupMenuAnchor(null);

                            // Set selected Activities Group
                            onActivitiesGroupSelected(activitiesGroup);
                          }}
                          value={activitiesGroup.id}
                          sx={{
                            height: '30px',
                            padding: '6px 10px',
                            flexDirection: 'row',
                            gap: '8px',
                            alignItems: 'center',
                            color: '#464646',
                            fontSize: '14px',
                            fontWeight: '500',
                            lineHeight: '10px',
                            letterSpacing: '0em',
                            textAlign: 'center',
                            '&:hover': {
                              backgroundColor: Pallete.blue.lighter,
                            },
                          }}
                        >
                          {activitiesGroup.name}
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              )}
              {authContext.state.session?.selectedAffiliation?.entity.specialPurpose ===
                EntitySpecialPurpose.Testing && (
                <AiButton
                  isLoading={isLoading}
                  onClick={() => {
                    onAiGeneratedRequest && onAiGeneratedRequest();
                  }}
                />
              )}
            </div>
          )}
        </Stack>
      }
    />
  );
};
